/**
 * Optional consent log: record cookie consent to DB on explicit action.
 * No PII beyond optional user_id. Uses anon_id when user not logged in.
 * Fire-and-forget; failures do not break UI.
 */

import { supabase } from "@/integrations/supabase/client";
import { getOrCreateAnonId } from "@/lib/anonId";
import type { CookieConsentState } from "@/contexts/CookieConsentContext";

const CONSENT_VERSION =
  (import.meta.env.VITE_CONSENT_VERSION as string) || "2026-02-09";

/**
 * Log consent to public.consent_logs. Resolves user_id and anon_id internally.
 * Call only on explicit action: acceptAll, rejectAll, or save from modal.
 */
export function logConsentToDb(preferences: CookieConsentState): void {
  const anonId = getOrCreateAnonId();
  const hasAnonId = anonId.length >= 8 && anonId.length <= 128;

  supabase.auth.getUser().then(({ data: { user } }) => {
    const userId = user?.id ?? null;
    if (!userId && !hasAnonId) return;

    const payload = {
      user_id: userId || null,
      anon_id: hasAnonId ? anonId : null,
      preferences: {
        essential: preferences.essential,
        analytics: preferences.analytics,
        marketing: preferences.marketing,
        version: preferences.version,
        updatedAt: preferences.updatedAt,
      },
      version: preferences.version || CONSENT_VERSION,
    };

    supabase
      .from("consent_logs")
      .insert(payload)
      .then(({ error }) => {
        if (error) {
          if (import.meta.env.DEV) {
            console.debug("[consentLog] Insert failed:", error.message);
          }
        }
      })
      .catch(() => {
        if (import.meta.env.DEV) {
          console.debug("[consentLog] Insert error (silent)");
        }
      });
  }).catch(() => {
    if (import.meta.env.DEV) {
      console.debug("[consentLog] getUser error (silent)");
    }
  });
}

export { CONSENT_VERSION };
